import React from 'react';
import { createActions } from 'redux-actions';
import { actionTypes } from './actionTypes';
import commonActions from './common';
import { v4 as uuidv4 } from 'uuid';
import { API } from '@service';
import { formatOverallComplexityResponse } from '@modules/questionnaires/complexity/sections/strategy/strategyComplexyUtils';
import { setSessionStorage, getSessionStorage } from '@utils/windowUtil';
import { deepCopy } from '@utils/common';
import { getValueFromSearchParam } from '@utils/history';
import { sortAtoZ } from '@utils/widget';
import action from '@flux/action/index';
import { getTeamsNames } from '@service/apis';
import { projectRole } from '@model';
import actions from '@flux/action/index';
import SearchParamKeys from '@model/searchParamKeys';
import { checkTabPermission } from '@utils/permission';
import { fillOriginPermission } from '@modules/systemSetting/systemModule/helper';
import { getUrlMap } from '@components/directUser/data';

const sortProjectList = (a, b) => {
  if (a.pinned === b.pinned) {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  }
  return a.pinned ? -1 : 1;
};

const clarifyPinStatus = (projectIds) => (d) =>
  Object.assign(d, { pinned: projectIds.includes(d.projectId) });

export const projectActions = createActions(
  {},
  ...Object.values(actionTypes.projects),
);
const fetchProjectsListData = (options) => async (dispatch, getState) => {
  dispatch(commonActions.changeLoading(true));

  // return;
  const { data, top } = await API.getProjectsList();

  const list = data.map(clarifyPinStatus(top)).sort(sortProjectList);

  // TODO: this should move to the login function
  if (list.length === 0) {
    dispatch(commonActions.changeShouldShowStatement(false));
  }
  dispatch(projectActions.changePinnedProjects(Array.isArray(top) ? top : []));
  dispatch(projectActions.changeProjectsList(Array.isArray(list) ? list : []));

  dispatch(commonActions.changeLoading(false));
};

const fetchArchiveListData = (options) => async (dispatch, getState) => {
  dispatch(commonActions.changeLoading(true));
  const { data } = await API.getProjectsArchive();

  dispatch(projectActions.changeArchiveList(data));
  dispatch(commonActions.changeLoading(false));
};

const deleteProjectFromList = (projectId) => async (dispatch, getState) => {
  const curData = getState().projects.listData;
  const data = curData.filter((e) => e.projectId !== projectId);
  dispatch(projectActions.changeProjectsList(Array.isArray(data) ? data : []));
};

const deleteProjectFromArchiveList =
  (projectId) => async (dispatch, getState) => {
    const curData = getState().projects.archiveListData;
    const data = curData.filter((e) => e.projectId !== projectId);
    dispatch(projectActions.changeArchiveList(Array.isArray(data) ? data : []));
  };

const changeProjectPinStatus = (projectId) => async (dispatch, getState) => {
  const curPinned = getState().projects.pinnedProjects || [];
  let newPinned;
  if (curPinned.includes(projectId)) {
    newPinned = curPinned.filter((e) => e !== projectId);
  } else {
    newPinned = [...curPinned, projectId];
  }

  dispatch(commonActions.changeLoading(true));
  await API.pinProjects(newPinned);

  const curList = getState().projects.listData || [];
  const newList = curList
    .map(clarifyPinStatus(newPinned))
    .sort(sortProjectList);

  dispatch(projectActions.changePinnedProjects(newPinned));
  dispatch(
    projectActions.changeProjectsList(Array.isArray(newList) ? newList : []),
  );
  dispatch(commonActions.changeLoading(false));
};

const addProject = (data) => async (dispatch, getState) => {
  dispatch(commonActions.changeLoading(true));
  const result = await API.createProject(data);
  if (!result) {
    window.scrollTo(0, 0);
    return { success: false, id: null };
  }
  dispatch(commonActions.changeLoading(false));
  return {
    success: Boolean(result.project),
    id: result.project,
    hasAuth: result.currentUserMembership,
    companyData: result,
  };
};

/**
 * @description search the displayname of company by the word through craft
 * @param {string} word
 */
const queryCompanyName = (word, type, isLoading, mode) => async (dispatch) => {
  if (isLoading === false) {
    dispatch(projectActions.createProjectDisabledHandler(true));
  } else {
    dispatch(projectActions.changeQueryCompanyLoading(true));
  }
  const result = await API.queryCreateProjectCompanyName(word, mode).catch(
    (err) => ({}),
  );

  if (result && result.found) {
    if (type) {
      if (result.found.items.length === 0) {
        dispatch(
          projectActions.saveData({
            buyerNotFound: type === 'buyerName',
            targetNotFound: type === 'targetName',
          }),
        );
      }
      dispatch(
        projectActions.saveQueryCompanyName({
          [type]: result.found.items,
        }),
      );
    }
  }
  dispatch(projectActions.changeQueryCompanyLoading(false));
  dispatch(projectActions.createProjectDisabledHandler(false));
};

const changeDUNSlistData = (dunsList) => (dispatch) => {
  dispatch(
    projectActions.dunsNumberListHandler({
      DUNsNumberList: dunsList,
    }),
  );
};

const queryDUNSCompanyNum = (word, pageSize) => async (dispatch) => {
  dispatch(projectActions.changeQueryCompanyLoading(true));
  const result = await API.queryDUNsNumAndName(word, pageSize).catch(
    (err) => ({}),
  );
  if (Array.isArray(result?.companyList)) {
    dispatch(
      projectActions.dunsNumberListHandler({
        DUNsNumberList: result,
      }),
    );
    dispatch(projectActions.changeQueryCompanyLoading(false));
  }
};

const clearCompanyName = (type) => async (dispatch) => {
  if (type) {
    dispatch(projectActions.clearCompanyName({ [type]: '' }));
  } else {
    dispatch(projectActions.clearCompanyName({ clear: true }));
  }
};

const saveData = (data) => async (dispatch) => {
  dispatch(projectActions.saveData(data));
};

const settingProjectData = (data) => async (dispatch) => {
  dispatch(projectActions.settingProjectData(data));
};

const saveIntegrations =
  ({ data = {}, tabLists, key, displayMode }) =>
  async (dispatch, getState) => {
    const {
      common: { currentId: projectId },
      projects: {
        projectDetail: { tabs = [] },
      },
    } = getState();

    const currentTabList = tabLists || tabs;

    const integrationModuleWithTabList = currentTabList.map((tab) => ({
      tabName: tab.text,
      tabId: tab.id,
      isDefaultTab: tab.deletable ? 0 : 1,
      display: tab.display,
      moduleList: getModuleListItem(data[tab.id]),
      // permission: getPermissionToAPI(tab.permission),

      ...getTilePermissionToAPI(tab.permission),
    }));

    const formData = {
      integrationNavigatorName: data.integrationNavigatorName,
      integrationModuleWithTabList,
      displayMode,
    };
    const result = await API.saveIntegrationRequest({
      formData,
      projectId,
    });

    return {
      success: result === 'SUCCESS',
    };
  };

const fetchProjectDetails =
  (projectId, options = {}) =>
  async (dispatch, getState) => {
    const skipGlobalLoading = options?.skipGlobalLoading === true;
    try {
      const currentId = projectId || getState().common.currentId;

      if (!currentId || projectId === 'null') {
        return;
      }
      if (!skipGlobalLoading) {
        dispatch(commonActions.changeLoading(true));
      }

      const projectCompanyInfo = getState().common.projectCompanyInfo;

      const [
        integrationData,
        complexityData,
        lifecycleData,
        // lifecycleData_divestiture,
      ] = await Promise.all([
        API.getIntergrationOverview(currentId),
        API.getComplexityBoardData(currentId),
        API.getLifecycleDataRequest(
          currentId,
          projectCompanyInfo.companyInstance,
        ),
        // API.getLifecycleDataRequest(currentId, 'Divestiture'),
      ]);

      if (
        !window.location.href.includes(integrationData?.projectInfo?.projectId)
      ) {
        return;
      }

      const formatIntegrationModuleListRes = formatIntegrationModuleList(
        integrationData?.integrationModuleWithTabList || [],
        dispatch,
        integrationData?.groupInfo,
      );

      dispatch(
        projectActions.saveProjectDetail(
          {
            originIntegrationData: integrationData,
            groupInfo: (integrationData?.groupInfo || []).sort((a, b) =>
              sortAtoZ(a?.groupName, b.groupName),
            ),
            projectInfo: integrationData?.projectInfo,
            ...formatIntegrationModuleListRes,
            complexityData: formatOverallComplexityResponse(complexityData),
            lifecycleData,
          } || {},
        ),
      );
      const projectName = integrationData?.projectInfo?.projectName;
      if (projectName) {
        dispatch(action.matchBreadCrumb(projectName));
        setSessionStorage('projectName', projectName);
      }
    } finally {
      if (!skipGlobalLoading) {
        dispatch(commonActions.changeLoading(false));
      }
    }
  };

const fetchAcceleratingTransition =
  (projectId) => async (dispatch, getState) => {
    try {
      const currentId = projectId || getState().common.currentId;

      if (!currentId) {
        return;
      }
      dispatch(projectActions.changeIntegrationNavigatorLoading(true));

      const integrationData = await API.getIntergrationOverview(currentId);
      const formatIntegrationModuleListRes = formatIntegrationModuleList(
        integrationData?.integrationModuleWithTabList || [],
        dispatch,
        integrationData?.groupInfo,
      );

      await dispatch(
        projectActions.saveProjectDetail(
          {
            originIntegrationData: integrationData,
            projectInfo: integrationData?.projectInfo,
            groupInfo: (integrationData?.groupInfo || []).sort((a, b) =>
              sortAtoZ(a?.groupName, b.groupName),
            ),
            ...formatIntegrationModuleListRes,
          } || {},
        ),
      );
    } finally {
      dispatch(projectActions.changeIntegrationNavigatorLoading(false));
    }
  };

const getTeamsSetting = (result) => (dispatch, getState) => {
  const members = result.members.map((item) => [item]);
  const res = [...members];
  const memberData = [...result.members];
  dispatch(projectActions.saveTeamsNames({ data: res, memberData }));
};

const getTeamsDefaultMembers = (userInfo) => async (dispatch, getState) => {
  dispatch(commonActions.changeLoading(true));
  const queryRes = await API.queryMemberInfoByGuid(userInfo.uid);
  const operatorInfo = (queryRes?.members || []).find(
    (e) => e.guid === userInfo.uid,
  );

  if (operatorInfo) {
    const memberInfo = {
      fullName: operatorInfo.fullName,
      email: operatorInfo.primaryEmail,
      country: operatorInfo.country,
      guid: operatorInfo.guid,
      staffLevel: operatorInfo.title || '',
      location: operatorInfo.location,
      department: operatorInfo.department,
      nonUS: operatorInfo.nonUS,
      isLead: false,
      isManager: false,
      isEUBased: null,
      permission: projectRole.Contributor,
    };
    const res = [[], [], [memberInfo]];
    const memberData = [memberInfo];
    dispatch(projectActions.saveTeamsNames({ data: res, memberData }));
  } else {
    dispatch(
      projectActions.saveTeamsNames({
        data: [''],
        memberData: new Array(1).fill({ permission: projectRole.Contributor }),
      }),
    );
  }
  dispatch(commonActions.changeLoading(false));
  return true;
};

const getProjectSetting = (projectId) => async (dispatch, getState) => {
  const projectId = getSessionStorage('currentId');
  if (!projectId) {
    return;
  }
  dispatch(commonActions.changeLoading(true));
  const result = await API.getProjectSetting(projectId);

  if (!result) {
    dispatch(commonActions.changeLoading(false));
    return null;
  }

  const instanceType = getState()?.common?.projectCompanyInfo?.instanceType;
  if (result.moduleInfo) {
    result.moduleInfo.forEach((i) => {
      i.groupPermissions = i.groupsPermission;
      if (Array.isArray(i.groupPermissions)) {
        i.groupPermissions.forEach(
          (j) =>
            (j.groupPermission =
              j.groupPermission?.filter((key) => Boolean(key)) || []),
        );
      }
      // old module data need to auto fill originPermissions
      fillOriginPermission(i, instanceType);
      Reflect.deleteProperty(i, 'groupsPermission');
      if (i.value === 'insight' && Array.isArray(i.originPermissions)) {
        i.originPermissions = i.originPermissions.filter(
          (i) => i.value !== 'insights',
        );
      }
    });
  }
  if (result.groups) {
    result.groups.forEach((i) => {
      if (Array.isArray(i.groupMembers)) {
        i.groupMembers.forEach((member) => {
          if (!member.groupId) {
            member.groupId = i.groupInfo.id;
          }
        });
      }
    });
  }

  if (result) {
    dispatch(getTeamsSetting(result.teamInfo));
  }

  dispatch(commonActions.changeLoading(false));
  return result;
};

const saveProjectSettings = (data) => async (dispatch, getState) => {
  dispatch(commonActions.changeLoading(true));
  const id = getSessionStorage('currentId');
  const result = await API.postProjectSetting(id, data);
  dispatch(commonActions.changeLoading(false));
  dispatch(getCustomizedModules(id));
  window.scrollTo(0, 0);
  if (!result) {
    return { success: false, id: null };
  }
  return {
    success: Boolean(result),
    hasAuth: result.currentUserMembership,
  };
};

const queryTeamsName = (p) => async (dispatch, getState) => {
  const { value, role } = p;
  const projectNamesResult = getState().projects.queryNamesResult;
  if (!value.trim()) {
    return;
  }

  dispatch(projectActions.changeQueryNamesLoading(true));

  dispatch(projectActions.changeTeamsNames({ data: [], role }));
  let results = await getTeamsNames(value);
  results = results && results.filter((item) => item.guid);
  let data;
  if (!results || results.length === 0) {
    data = [];
  } else {
    data = results.map((d) => ({
      fullName: d.name,
      email: d.email,
      country: d.country,
      guid: d.guid,
      staffLevel: d.staff || '',
      location: d.workLoc,
      department: d.jobFunc,
      nonUS: d.nonUS,
      isLead: role === 0 ? true : false,
      isManager: role === 1 ? true : false,
      isEUBased: null,
    }));
  }
  const res = [...projectNamesResult];
  res[role] = data;

  dispatch(projectActions.changeQueryNamesLoading(false));
  dispatch(projectActions.changeTeamsNames({ data: res || [] }));
};

const handlePermission = (result, type) => {
  if (!result || !result.customizeModule) {
    return [];
  }
  return result.customizeModule
    .reduce((acc, e) => [...acc, ...e[type].map((p) => p.value)], [])
    .filter((e, i, a) => a.indexOf(e) === i);
};

const getCustomizedModules =
  (id, options = {}) =>
  async (dispatch, getState) => {
    const skipGlobalLoading = options?.skipGlobalLoading === true;
    if (!skipGlobalLoading) {
      dispatch(actions.changeLoading(true));
    }
    try {
      const result = await API.getCustomizedModule(id);
      // TODO: replace all usage of clientPermissions and internalPermissions, use accessibleModules to check permission instead
      const clientPermission = handlePermission(result, 'clientPermissions');
      const internalPermission = handlePermission(result, 'internalPermissions');
      dispatch(
        projectActions.saveUserPermission({
          type: 'internalPermission',
          detailType: 'internalSectionsPermission',
          permission: internalPermission,
        }),
      );
      dispatch(
        projectActions.saveUserPermission({
          type: 'userPermission',
          detailType: 'sectionsPermission',
          permission: clientPermission,
        }),
      );

      const instanceType = getState()?.common?.projectCompanyInfo?.instanceType;
      if (result?.customizeModule?.length) {
        // old module data need to auto fill originPermissions
        result.customizeModule.forEach((i) => {
          fillOriginPermission(i, instanceType);
        });
      }
      dispatch(projectActions.saveCustomizedModules(result));
      dispatch(commonActions.changeUrlMap(getUrlMap(result?.customizeModule)));
    } catch (e) {
      console.log(e);
    }
    if (!skipGlobalLoading) {
      dispatch(actions.changeLoading(false));
    }
  };

const submitLifecycleData = (postBody, type) => async (dispatch, getState) => {
  dispatch(commonActions.changeLoading(true));

  const projectId = getState().common.currentId;

  const response = await API.updateLifecyleDataRequset(
    projectId,
    postBody,
    type,
  );

  if (!response?.message?.includes('success')) {
    return { success: false };
  }

  await dispatch(fetchLifecycleData(projectId, type));

  dispatch(commonActions.changeLoading(false));

  return { success: true };
};

const fetchLifecycleData = (projectId, type) => async (dispatch, getState) => {
  const currentId = projectId || getState().common.currentId;
  if (!currentId || !projectId) {
    return;
  }
  const res = await API.getLifecycleDataRequest(currentId, type);
  if (!res) {
    return;
  }
  dispatch(projectActions.saveProjectDetail({ lifecycleData: res }));
};

const moduleMap = {
  Home: 'Project Home',
  companyoverview: 'Company Overview',
  financials: 'Financials',
  gotomarket: 'Go-To-Market',
  operationalfootprint: 'Operational Footprint',
  sentimentanalysis: 'Sentiment Analysis',
  'integration-approach': 'Integration Approach',
  'separation-approach': 'Separation Approach',
  'value-driver': 'Value Drivers',
  maturityAssessment: 'Maturity Assessment',
  guidingPrinciples: 'Integration Guiding Principles',
  'guidingPrinciples-divestitures': 'Divestiture Guiding Principles',
  dealrationale: 'Deal Rationale',
  esgAndCsr: 'ESG',
  cyberSecurity: 'Cyber Security',
  OrgChart: 'Governance Structure',
  GanttChart: 'Deal Timeline',
};

// const homeComponentsMap = {
//   complexity: 'Deal Complexity',
//   navigator: 'Accelerating the Transition',
//   lifecycle: 'M&A Lifecycle - Integration',
//   lifecycleDivestitures: 'M&A Lifecycle - Divestiture',
// };

const getAccessibleModules = (projectID) => async (dispatch, getState) => {
  const res = await API.getProjectAccessibleModules(projectID);
  if (!res) {
    return;
  }
  if (Array.isArray(res.moduleList)) {
    res.moduleList = res.moduleList.filter((i) => i !== 'insights');
  }

  const customizedModules = getState()?.projects?.customizedModules || [];
  const insightsAndAnalysisSubmodules = customizedModules
    .filter((i) => ['insight', 'analysis'].includes(i.value))
    .flatMap((i) => i.originPermissions)
    .filter((i) => i);
  let accessibles = Object.keys(moduleMap)
    .filter((k) => res.moduleList.includes(k))
    .reduce((acc, e) => {
      if (moduleMap[e] === 'Project Home') {
        return Object.assign({}, acc, {
          // [moduleMap[e]]: res.homepageSectionList.map(
          //   (section) => homeComponentsMap[section] || section,
          // ),
          [moduleMap[e]]: res.homepageSectionList,
        });
      }
      const moduleName =
        insightsAndAnalysisSubmodules?.find((i) => i.value === e)?.label ||
        moduleMap[e];
      return Object.assign({}, acc, { [moduleName]: '*' });
    }, {});

  accessibles._otherModule = res.moduleList?.filter((i) => !moduleMap[i]);
  accessibles = Object.assign(accessibles, res);

  dispatch(projectActions.saveAccessibleModules(accessibles));
  dispatch(projectActions.saveOriginAccessibleModules(res));
  dispatch(projectActions.savePermissionBundleProjectId(projectID));
};

const updateAccessibleModules =
  ({ module, identifier }) =>
  async (dispatch, getState) => {
    const curModules = deepCopy(getState().projects.accessibleModules);

    // const component = homeComponentsMap[identifier] || identifier;
    const component = identifier;
    let moduleComponents = curModules[module] || [];

    if (!moduleComponents.includes(component)) {
      moduleComponents.push(component);
    } else {
      moduleComponents = moduleComponents.filter((comp) => comp !== component);
    }

    curModules[module] = moduleComponents;

    dispatch(projectActions.saveAccessibleModules(curModules));
  };

export default {
  ...projectActions,
  fetchProjectsListData,
  deleteProjectFromList,
  addProject,
  queryCompanyName,
  queryDUNSCompanyNum,
  changeDUNSlistData,
  saveIntegrations,
  fetchProjectDetails,
  queryTeamsName,
  getTeamsSetting,
  saveProjectSettings,
  getProjectSetting,
  getTeamsDefaultMembers,
  saveData,
  settingProjectData,
  clearCompanyName,
  changeProjectPinStatus,
  fetchArchiveListData,
  deleteProjectFromArchiveList,
  submitLifecycleData,
  getCustomizedModules,
  getAccessibleModules,
  updateAccessibleModules,
  fetchAcceleratingTransition,
};

export const getModuleListItem = (list = [], index) =>
  list.map((item, index) => ({
    moduleId: item.moduleId,
    tabId: item.tabId,
    module: item.module,
    display: Boolean(item.display),
    iframe: Boolean(item.iframe),
    order: index + 1,
    color: item.color,
    link: item.link || '',
    description: item.description || '',
    type: item.type || 'text',
    dataSourceLink: item.dataSourceLink || null,
    ratio: item.ratio || null,
  }));

export const formatIntegrationModuleList = (
  listData = [],
  dispatch,
  groupInfo = [],
) => {
  const { integrationTabId } = getValueFromSearchParam(
    SearchParamKeys.integrationTabId,
  );
  const userInfo = JSON.parse(window.localStorage.userInfo || '{}') || {};

  const result = listData.reduce(
    (result, item = {}, i) => {
      const moduleList = item.moduleList || item.integrationModules;
      const tabId = item.tabId || moduleList[0]?.tabId || uuidv4();
      const tabItem = {
        text: item.tabName,
        id: tabId,
        deletable: !item.isDefaultTab,
        showable: moduleList.filter((mod) => mod.display).length > 0,
        // permission: getPermissionFromAPI(item.permission),
        permission: getTilePermissionFromAPI(
          item.permission,
          item.groupPermission,
        ),
        display: item.display,
      };

      result.tabs.push(tabItem);
      result.integrationModuleData[tabId] = moduleList;
      if (
        integrationTabId === tabId &&
        checkTabPermission(tabItem.permission, groupInfo, userInfo)
      ) {
        result.currentTab = tabId;
      }
      return result;
    },
    { tabs: [], integrationModuleData: {}, currentTab: null },
  );
  if (!result.currentTab) {
    result.currentTab = result.tabs?.[0]?.id;
    if (integrationTabId && dispatch) {
      dispatch(
        commonActions.showBanner({
          status: 'warning',
          content: (
            <div>
              Sorry. You don’t have the permission to open the page.
              <br />
              For further questions, please contact the PwC M&A Gateway team at{' '}
              <a
                href="mailto:us_dt_solution_innovation@pwc.com"
                target="_blank"
                rel="noreferrer"
              >
                us_dt_solution_innovation@pwc.com
              </a>
            </div>
          ),
          closeable: true,
        }),
      );
      setTimeout(() => {
        dispatch(commonActions.closeBanner());
      }, 5000);
    }
  }
  return result;
};

export const getPermissionToAPI = (permission) => {
  if (!Array.isArray(permission) || permission.length === 0) {
    return 'None';
  }
  if (permission.length > 1) {
    return 'Both';
  }
  return {
    client: 'Client Only',
    internal: 'Internal Only',
  }[permission[0]];
};

const PermissionMap = {
  'Internal Only': ['internal'],
  'Client Only': ['client'],
  Both: ['client', 'internal'],
  None: [],
};

const getPermissionFromAPI = (permission) => PermissionMap[permission];

export const getTilePermissionToAPI = (values) => {
  const defaultPermission = values.filter((i) =>
    ['client', 'internal'].includes(i),
  );
  const groupPermission = values.filter(
    (i) => !['client', 'internal'].includes(i),
  );
  return {
    permission: getPermissionToAPI(defaultPermission),
    groupPermission,
  };
};

export const getTilePermissionFromAPI = (
  defaultPermission,
  groupPermission = [],
) => [...getPermissionFromAPI(defaultPermission), ...groupPermission];
